import { z } from 'zod';
import { LIMITS } from '../limits.js';

/**
 * My read state in one channel (guild channel, DM or group DM): the stored
 * pointer and what is newer than it. Private — the server only ever sends it
 * to its owner's own sockets (`ready.readStates`, the `read:state` event).
 *
 * `unread` counts main-list messages by other people after the pointer (not
 * thread comments, service lines, deleted or hidden-for-me messages);
 * `mentions` is the subset that @-mentions me or replies to my message. Both
 * stop at `LIMITS.readState.countCap`.
 */
const countSchema = z.number().int().min(0).max(LIMITS.readState.countCap);

export const channelReadStateSchema = z.object({
  channelId: z.string().min(1),
  lastReadMessageId: z.string().min(1),
  unread: countSchema,
  mentions: countSchema,
  /**
   * ISO time of the newest message these counts account for (the pointer's own
   * message time when nothing is newer). A live `channel:activity` /
   * `dm:activity` not newer than this is already in the counts; a newer one is
   * not — clients add those on top instead of losing them to a late snapshot.
   */
  countedThrough: z.string().datetime(),
});
export type ChannelReadState = z.infer<typeof channelReadStateSchema>;

/** Body of `POST /api/channels/:channelId/read`. */
export const markChannelReadSchema = z.object({
  messageId: z.string().cuid(),
});
export type MarkChannelReadInput = z.infer<typeof markChannelReadSchema>;
